import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LifeBuoy, MessageSquare, TicketCheck, User } from 'lucide-react';
import { auth } from '../../lib/firebase';
import ApplicantChatView from '../ApplicantChatView';
import {
  compareConversationListOrder,
  ContextRow,
  ContextSection,
  ConversationList,
  MessagingEmptyState,
  MessagingErrorState,
  MessagingLayout,
  MessagingStrip,
  MessagingTag,
  ThreadFrame,
} from '../messaging';
import type { MessagingConversation } from '../messaging';

/**
 * Enhanced Referrer support-ticket inbox.
 *
 * Enhanced referrers can already see (server-side) the support tickets that
 * belong to applicants they referred, and can already post/react to comments
 * on those tickets — but until now there was no referrer-facing UI to reach
 * this surface at all. This view lists those tickets and lets the referrer
 * open one to read/reply, reusing the same design system (avatars, ticket
 * metadata, attachments, mobile/desktop layout) as the applicant + admin
 * ticket surfaces so it is visually and behaviourally consistent.
 *
 * Referrers cannot open/close tickets, change priority, or add internal
 * notes — those remain admin-only actions (enforced server-side too).
 */

interface Ticket {
  ticketId: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  applicantId?: string;
  applicantName?: string;
  applicantEmail?: string;
  programName?: string;
  assignedToName?: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

interface Props {
  /** Injected so this view shares the same auth/workspace-aware fetch helper as the rest of the referrer dashboard. */
  safeFetch: (url: string, options?: any) => Promise<any>;
}

function statusTone(status?: string): 'neutral' | 'accent' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'open': return 'accent';
    case 'in_progress': return 'warning';
    case 'resolved': return 'success';
    case 'closed': return 'neutral';
    default: return 'neutral';
  }
}

function priorityTone(priority?: string): 'neutral' | 'accent' | 'success' | 'warning' | 'danger' {
  switch (priority) {
    case 'urgent':
    case 'high': return 'danger';
    case 'medium': return 'warning';
    case 'low': return 'success';
    default: return 'neutral';
  }
}

function formatLabel(value?: string): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
}

function ticketToConversation(ticket: Ticket): MessagingConversation {
  return {
    id: ticket.ticketId,
    name: ticket.title || 'Support ticket',
    subtitle: ticket.applicantName || ticket.applicantEmail || '',
    preview: ticket.description || 'No description provided',
    timestamp: ticket.updatedAt || ticket.createdAt,
    // Chat Phase 4: the caller's own per-viewer pin/archive state,
    // projected by GET /api/tickets (never the shared lifecycle flag).
    pinned: !!ticket.viewerPinned,
    archived: !!ticket.viewerArchived,
    status: ticket.status,
    tags: ticket.priority ? [ticket.priority] : undefined,
    data: ticket,
  };
}

export default function ReferrerSupportView({ safeFetch }: Props) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [box, setBox] = useState<'active' | 'archived'>('active');
  const [actionError, setActionError] = useState<string | null>(null);

  const loadTickets = useCallback(async () => {
    setError(null);
    try {
      const data = await safeFetch('/api/tickets');
      setTickets(Array.isArray(data?.tickets) ? data.tickets : []);
    } catch (err: any) {
      setError(err?.message || 'Could not load support tickets.');
    } finally {
      setLoading(false);
    }
  }, [safeFetch]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const conversations = useMemo(
    // Chat Phase 4: pinned tickets first, then newest activity.
    () => tickets.map(ticketToConversation).sort(compareConversationListOrder),
    [tickets]
  );

  const filtered = useMemo(() => {
    const inBox = conversations.filter((c) => (box === 'archived' ? c.archived : !c.archived));
    if (!search.trim()) return inBox;
    const q = search.trim().toLowerCase();
    return inBox.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      (c.subtitle || '').toLowerCase().includes(q) ||
      (c.preview || '').toLowerCase().includes(q)
    );
  }, [conversations, search, box]);

  /* Chat Phase 4: per-viewer pin/archive on the ticket. Optimistic flip;
     a failed write rolls back — the list never shows a state the server
     did not accept. Archiving is personal: the applicant and the admin
     team keep seeing the ticket exactly as before. */
  const toggleTicketState = useCallback(
    async (conversation: MessagingConversation, field: 'pinned' | 'archived') => {
      const ticketId = conversation.id;
      const serverField = field === 'pinned' ? 'viewerPinned' : 'viewerArchived';
      const nextValue = !conversation[field];
      setTickets((prev) => prev.map((t) => (t.ticketId === ticketId ? { ...t, [serverField]: nextValue } : t)));
      try {
        await safeFetch(`/api/tickets/${ticketId}/state`, {
          method: 'POST',
          body: JSON.stringify({ [field]: nextValue }),
        });
      } catch {
        setTickets((prev) => prev.map((t) => (t.ticketId === ticketId ? { ...t, [serverField]: !nextValue } : t)));
        setActionError('Could not update the ticket — the change was not saved.');
      }
    },
    [safeFetch]
  );

  const selectedTicket = useMemo(
    () => tickets.find((t) => t.ticketId === selectedId) || null,
    [tickets, selectedId]
  );

  const list = (
    <>
      {actionError && (
        <MessagingStrip tone="danger" onDismiss={() => setActionError(null)}>
          {actionError}
        </MessagingStrip>
      )}
      <ConversationList
      title="Support"
      subtitle="Tickets for your referred applicants"
      conversations={filtered}
      selectedId={selectedId}
      onSelect={(conversation) => setSelectedId(conversation.id)}
      loading={loading}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search tickets…"
      applySearchFilter={false}
      filters={[
        { id: 'active', label: 'Active', count: conversations.filter((c) => !c.archived).length },
        { id: 'archived', label: 'Archived', count: conversations.filter((c) => c.archived).length },
      ]}
      activeFilterId={box}
      onFilterChange={(id) => setBox(id as 'active' | 'archived')}
      onTogglePin={(conversation) => void toggleTicketState(conversation, 'pinned')}
      onToggleArchive={(conversation) => void toggleTicketState(conversation, 'archived')}
      emptyTitle={box === 'archived' ? 'No archived tickets' : 'No support tickets'}
      emptyDescription={
        box === 'archived'
          ? 'Tickets you archive live here. Archiving only affects your list — the applicant and the support team still see the ticket.'
          : 'Tickets opened for the applicants you referred will appear here.'
      }
      ariaLabel="Support tickets"
      />
    </>
  );

  const thread = error ? (
    <ThreadFrame>
      <MessagingErrorState
        title="Could not load tickets"
        description={error}
        onRetry={() => { setLoading(true); loadTickets(); }}
      />
    </ThreadFrame>
  ) : selectedTicket ? (
    <ApplicantChatView
      key={`referrer-support-${selectedTicket.ticketId}`}
      chatType="support"
      ticket={selectedTicket}
      applicantName={selectedTicket.applicantName}
      userId={auth.currentUser?.uid || ''}
      participant={{ kind: 'referrer', id: auth.currentUser?.uid || '', applicationId: selectedTicket.applicationId }}
      preferApiOnly
      onClose={() => setSelectedId(null)}
      mode="split"
      embedded
      status={selectedTicket.status}
      onTicketUpdated={(ticketId, updates) => {
        setTickets((prev) => prev.map((t) => (t.ticketId === ticketId ? { ...t, ...updates } : t)));
      }}
    />
  ) : (
    <ThreadFrame>
      <MessagingEmptyState
        title="Select a ticket"
        description="Choose a support ticket from the list to read and reply to the conversation."
        icon={<MessageSquare size={28} strokeWidth={1.75} />}
      />
    </ThreadFrame>
  );

  const context = selectedTicket ? (
    <>
      <ContextSection label="Ticket details" icon={<TicketCheck size={12} />}>
        <ContextRow>
          <MessagingTag tone={statusTone(selectedTicket.status)}>{formatLabel(selectedTicket.status) || 'Open'}</MessagingTag>
          {selectedTicket.priority && (
            <MessagingTag tone={priorityTone(selectedTicket.priority)}>{formatLabel(selectedTicket.priority)} priority</MessagingTag>
          )}
        </ContextRow>
        {selectedTicket.programName && <ContextRow>Program: {selectedTicket.programName}</ContextRow>}
        <ContextRow icon={<User size={12} />}>
          {selectedTicket.assignedToName ? `Assigned to ${selectedTicket.assignedToName}` : 'Not yet assigned'}
        </ContextRow>
      </ContextSection>
      <ContextSection label="Applicant" icon={<LifeBuoy size={12} />}>
        <ContextRow>{selectedTicket.applicantName || selectedTicket.applicantEmail || 'Unknown applicant'}</ContextRow>
      </ContextSection>
    </>
  ) : null;

  return (
    <MessagingLayout
      list={list}
      thread={thread}
      context={context}
      hasSelection={!!selectedTicket}
      selectedConversationId={selectedId}
      style={{ height: '100%' }}
    />
  );
}
